import { ChangeDetectionStrategy, Component, computed, input, model, output, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { S25Profile } from "../../../../ProfileI";
import {
    COPY_NEW_SEGMENT_KEY,
    copySegmentPatternChangeWarning,
    copySegmentProfileOptions,
    newSegmentNameValidationError,
    segmentOptionId,
    segmentOptionKey,
    segmentOptionLabel,
} from "./occurrence.copy.segment.util";

@Component({
    selector: "occurrence-copy-segment-target",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule],
    styleUrls: ["./occurrence.copy.segment.target.component.scss"],
    template: `
        <section class="copy-event-section" aria-labelledby="copy-segment-target-heading">
            <div class="copy-event-section__row">
                <div id="copy-segment-target-heading" class="copy-event-section__title copy-event-section__title--row">
                    Copy to segment
                </div>
                <select
                    class="cn-form__control copy-event-section__segment-select"
                    [id]="copySegmentSelectId"
                    [name]="copySegmentSelectId"
                    aria-labelledby="copy-segment-target-heading"
                    [(ngModel)]="copyTargetProfileKey"
                    (ngModelChange)="onTargetKeyChange()"
                >
                    @for (profile of segmentOptions(); track segmentOptionId(profile)) {
                        <option [ngValue]="segmentOptionKey(profile)">
                            {{ segmentOptionLabel(profile) }}
                        </option>
                    }
                    <option [ngValue]="copyNewSegmentKey">New Segment</option>
                </select>
            </div>
            @if (copyTargetProfileKey() === copyNewSegmentKey) {
                <label class="copy-event-section__new-name-label" [attr.for]="newSegmentNameInputId">
                    New segment name<span class="required-indicator">*</span>
                </label>
                <input
                    class="cn-form__control copy-event-section__new-name-input"
                    [class.copy-event-section__new-name-input--invalid]="!!newSegmentNameError()"
                    type="text"
                    [id]="newSegmentNameInputId"
                    [(ngModel)]="newSegmentName"
                    (ngModelChange)="onNewSegmentNameChange()"
                    (blur)="newSegmentNameTouched.set(true)"
                    maxlength="40"
                    required
                    [attr.aria-invalid]="newSegmentNameError() ? true : null"
                    [attr.aria-describedby]="newSegmentNameError() ? newSegmentNameErrorId : null"
                />
                @if (newSegmentNameError(); as nameError) {
                    <p class="field-error copy-event-section__name-error" [id]="newSegmentNameErrorId" role="alert">
                        {{ nameError }}
                    </p>
                }
            }
            @if (patternWarning(); as patternWarningText) {
                <section class="cn-alert cn-alert--danger copy-event-section__pattern-warning">
                    <section class="cn-alert__icon cn-icon" name="alert--danger">
                        {{ patternWarningText }}
                    </section>
                </section>
            }
        </section>
    `,
})
export class OccurrenceCopySegmentTargetComponent {
    private static nextFormId = 0;

    readonly segmentProfiles = input<S25Profile[]>([]);
    readonly sourceProfileId = input<number | undefined>();
    readonly repeatPatternOnSource = input(false);
    readonly saveValidationAttempted = input(false);

    readonly copyTargetProfileKey = model("");
    readonly newSegmentName = model("");

    readonly targetKeyChange = output<void>();
    readonly newSegmentNameEdited = output<void>();

    readonly copyNewSegmentKey = COPY_NEW_SEGMENT_KEY;
    readonly copySegmentSelectId = `copy-segment-select-${OccurrenceCopySegmentTargetComponent.nextFormId++}`;
    readonly newSegmentNameInputId = `copy-new-segment-name-${OccurrenceCopySegmentTargetComponent.nextFormId++}`;
    readonly newSegmentNameErrorId = `${this.newSegmentNameInputId}-error`;

    readonly newSegmentNameTouched = signal(false);

    readonly segmentOptions = computed(() => copySegmentProfileOptions(this.segmentProfiles()));

    readonly patternWarning = computed(() =>
        copySegmentPatternChangeWarning({
            copyTargetProfileKey: this.copyTargetProfileKey(),
            copySourceProfileId: this.sourceProfileId(),
            profiles: this.segmentProfiles(),
            repeatPatternOnSource: this.repeatPatternOnSource(),
        }),
    );

    readonly newSegmentNameError = computed(() => {
        if (this.copyTargetProfileKey() !== COPY_NEW_SEGMENT_KEY) {
            return undefined;
        }
        if (!this.newSegmentNameTouched() && !this.saveValidationAttempted()) {
            return undefined;
        }
        return newSegmentNameValidationError(this.newSegmentName());
    });

    readonly segmentOptionId = segmentOptionId;
    readonly segmentOptionKey = segmentOptionKey;
    readonly segmentOptionLabel = segmentOptionLabel;

    onTargetKeyChange(): void {
        if (this.copyTargetProfileKey() !== COPY_NEW_SEGMENT_KEY) {
            this.newSegmentNameTouched.set(false);
        }
        this.targetKeyChange.emit();
    }

    onNewSegmentNameChange(): void {
        this.newSegmentNameEdited.emit();
    }
}
